import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { site } from "@/lib/siteContent";

export default function People() {
  const p = site.people;
  const c = site.careers;
  return (
    <section
      id="people"
      data-testid="people-section"
      className="relative bg-ash py-24 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          {/* Left: image + headline */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 relative"
          >
            <div className="relative overflow-hidden">
              <img
                src={p.image}
                alt="Our diverse team"
                className="w-full h-[480px] lg:h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10">
                <div className="text-[11px] font-bold uppercase tracking-[0.32em] text-gold mb-4">
                  Our People
                </div>
                <h2 className="font-display text-3xl lg:text-5xl text-white font-medium leading-tight">
                  {p.title.split(".")[0]}.<br />
                  <span className="italic text-gold">{p.title.split(".")[1]}.</span>
                </h2>
              </div>
            </div>
          </motion.div>

          {/* Right: bullets */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 bg-navy text-white p-10 lg:p-12 flex flex-col"
          >
            <p className="text-white/80 leading-relaxed text-base lg:text-lg">
              {p.description}
            </p>

            <ul className="mt-10 space-y-4">
              {p.bullets.map((b, i) => (
                <li key={b} data-testid={`people-bullet-${i}`} className="flex items-start gap-4">
                  <span className="mt-1 shrink-0 w-5 h-5 border border-gold/60 flex items-center justify-center">
                    <Check size={12} className="text-gold" strokeWidth={3} />
                  </span>
                  <span className="text-sm lg:text-base text-white/90">{b}</span>
                </li>
              ))}
            </ul>

            {/* Careers block */}
            <div className="mt-12 pt-10 border-t border-white/15">
              <div className="text-[11px] font-bold uppercase tracking-[0.32em] text-gold mb-3">
                Careers at DSS
              </div>
              <h3 className="font-display text-2xl lg:text-3xl text-white font-medium">
                {c.description}
              </h3>
              <div className="mt-6 flex flex-wrap gap-2">
                {c.bullets.map((b) => (
                  <span
                    key={b}
                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/80 border border-white/20 px-3 py-2"
                  >
                    <span className="w-1.5 h-1.5 bg-gold" />
                    {b}
                  </span>
                ))}
              </div>
              <a
                href="#contact"
                data-testid="careers-cta"
                className="mt-8 group inline-flex items-center gap-2 text-gold hover:text-gold-light text-[12px] font-bold uppercase tracking-[0.22em]"
              >
                Join Our Team
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
